import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  Typography,
} from "@mui/material";

function TableRegisters({ Registers, Programmes }) {
  // Group data by ProgrammeDate and ProgrammeTitle with counts
  const groupedData = Registers.reduce((acc, curr) => {
    const { ProgrammeDate, ProgrammeTitle, ProgrammeVarient } = curr.BasicInfo;
    const key = `${ProgrammeDate}-${ProgrammeTitle}`; // Unique key for each combination

    if (!acc[key]) {
      acc[key] = {
        ProgrammeDate,
        ProgrammeTitle,
        count: 0,
        specialCount: 0, // Count for entries with "2 week" or "premium"
        premiumCount: 0, // Count for entries with "premium"
        oneWeekAccomCount: 0, // Count for entries with "1 week" and "accom"
        twoWeekAccomCount: 0, // Count for entries with "2 week" and "accom"
        premiumAccomCount: 0, // Count for entries with "premium" and "accom"
        premiumOrTwoWeekAccomCount: 0, // Count for entries with "premium" or "2 week" and "accom"
        oneWeekCapacity: 0, // Placeholder for capacity
        twoWeekCapacity: 0, // Placeholder for capacity
      };
    }

    acc[key].count += 1; // Increment the total count for this combination

    // Increment the special count if ProgrammeVarient contains "2 week" or "premium"
    if (
      ProgrammeVarient.toLowerCase().includes("2 week") ||
      ProgrammeVarient.toLowerCase().includes("premium")
    ) {
      acc[key].specialCount += 1;
    }

    // Increment the premium count
    if (ProgrammeVarient.toLowerCase().includes("premium")) {
      acc[key].premiumCount += 1;
    }

    // Increment the count for "1 week" and "accom"
    if (
      ProgrammeVarient.toLowerCase().includes("1 week") &&
      ProgrammeVarient.toLowerCase().includes("accom")
    ) {
      acc[key].oneWeekAccomCount += 1;
    }

    // Increment the count for "2 week" and "accom"
    if (
      ProgrammeVarient.toLowerCase().includes("2 week") &&
      ProgrammeVarient.toLowerCase().includes("accom")
    ) {
      acc[key].twoWeekAccomCount += 1;
    }

    // Increment the count for "premium" and "accom"
    if (
      ProgrammeVarient.toLowerCase().includes("premium") &&
      ProgrammeVarient.toLowerCase().includes("accom")
    ) {
      acc[key].premiumAccomCount += 1;
    }

    // Increment the count for "premium" or "2 week" and "accom"
    if (
      (ProgrammeVarient.toLowerCase().includes("premium") ||
        ProgrammeVarient.toLowerCase().includes("2 week")) &&
      ProgrammeVarient.toLowerCase().includes("accom")
    ) {
      acc[key].premiumOrTwoWeekAccomCount += 1;
    }

    return acc;
  }, {});

  // Enhance groupedData with Capacity information from Programmes
  Object.values(groupedData).forEach((entry) => {
    const matchingProgrammes = Programmes.filter(
      (programme) =>
        programme.Title === entry.ProgrammeTitle &&
        programme.Dates === entry.ProgrammeDate &&
        programme.Capacity &&
        Number(programme.Capacity) > 0
    );

    // Find "1 week" and "2 week" capacities
    const oneWeekProgramme = matchingProgrammes.find(
      (programme) => programme.Length === "1 week"
    );
    const twoWeekProgramme = matchingProgrammes.find(
      (programme) => programme.Length === "2 week"
    );

    entry.oneWeekCapacity = oneWeekProgramme?.Capacity || 0;
    entry.twoWeekCapacity = twoWeekProgramme?.Capacity || 0;
  });

  // Convert groupedData to an array and sort alphabetically by ProgrammeDate and ProgrammeTitle
  const sortedData = Object.values(groupedData).sort((a, b) => {
    // Sort by ProgrammeDate alphabetically
    const dateComparison = b.ProgrammeDate.localeCompare(a.ProgrammeDate);
    if (dateComparison !== 0) {
      return dateComparison;
    }

    // If ProgrammeDate is the same, sort by ProgrammeTitle alphabetically
    return a.ProgrammeTitle.localeCompare(b.ProgrammeTitle);
  });

  // Calculate grand totals for all columns
  const grandTotals = sortedData.reduce(
    (acc, entry) => {
      acc.count += entry.count;
      acc.specialCount += entry.specialCount;
      acc.premiumCount += entry.premiumCount;
      acc.oneWeekAccomCount += entry.oneWeekAccomCount;
      acc.twoWeekAccomCount += entry.twoWeekAccomCount;
      acc.premiumAccomCount += entry.premiumAccomCount;
      acc.premiumOrTwoWeekAccomCount += entry.premiumOrTwoWeekAccomCount;
      acc.oneWeekCapacity += entry.oneWeekCapacity;
      acc.twoWeekCapacity += entry.twoWeekCapacity;
      return acc;
    },
    {
      count: 0,
      specialCount: 0,
      premiumCount: 0,
      oneWeekAccomCount: 0,
      twoWeekAccomCount: 0,
      premiumAccomCount: 0,
      premiumOrTwoWeekAccomCount: 0,
      oneWeekCapacity: 0,
      twoWeekCapacity: 0,
    }
  );

  //-----------------------------Return---------------------------------------------

  return (
    <TableContainer
      component={Paper}
      sx={{
        width: "100%",
        margin: "0 auto",
        overflow: "auto",
      }}
    >
      <Table
        aria-label="Programme Table"
        sx={{
          border: "1px solid #e0e0e0",
          tableLayout: "fixed",
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                fontWeight: "bold",
                backgroundColor: "#f5f5f5",
                textAlign: "center",
              }}
            >
              Programme Date
            </TableCell>
            <TableCell
              sx={{
                fontWeight: "bold",
                backgroundColor: "#f5f5f5",
                textAlign: "center",
              }}
            >
              Programme Title
            </TableCell>
            <TableCell
              sx={{
                fontWeight: "bold",
                backgroundColor: "#f5f5f5",
                textAlign: "center",
              }}
            >
              1 Week
            </TableCell>
            <TableCell
              sx={{
                fontWeight: "bold",
                backgroundColor: "#f5f5f5",
                textAlign: "center",
              }}
            >
              2 Weeks
            </TableCell>
            <TableCell
              sx={{
                fontWeight: "bold",
                backgroundColor: "#f5f5f5",
                textAlign: "center",
              }}
            >
              Premium
            </TableCell>
            <TableCell
              sx={{
                fontWeight: "bold",
                backgroundColor: "#f5f5f5",
                textAlign: "center",
              }}
            >
              1 Week Only & Accom Count
            </TableCell>
            <TableCell
              sx={{
                fontWeight: "bold",
                backgroundColor: "#f5f5f5",
                textAlign: "center",
              }}
            >
              2 Weeks Only (Non-Prem) & Accom Count
            </TableCell>
            <TableCell
              sx={{
                fontWeight: "bold",
                backgroundColor: "#f5f5f5",
                textAlign: "center",
              }}
            >
              Premium & Accom Count
            </TableCell>
            <TableCell
              sx={{
                fontWeight: "bold",
                backgroundColor: "#f5f5f5",
                textAlign: "center",
              }}
            >
              Premium or 2 Week & Accom Count
            </TableCell>
            <TableCell
              sx={{
                fontWeight: "bold",
                backgroundColor: "#f5f5f5",
                textAlign: "center",
              }}
            >
              1 Week Capacity
            </TableCell>
            <TableCell
              sx={{
                fontWeight: "bold",
                backgroundColor: "#f5f5f5",
                textAlign: "center",
              }}
            >
              2 Week Capacity
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {/* Add the Grand Total Row */}
          <TableRow>
            <TableCell
              colSpan={2}
              sx={{
                textAlign: "center",
                fontWeight: "bold",
                backgroundColor: "#f0f0f0",
                fontSize: "1.4rem",
              }}
            >
              Grand Total
            </TableCell>
            <TableCell
              sx={{
                textAlign: "center",
                fontWeight: "bold",
                fontSize: "1.4rem",
              }}
            >
              {grandTotals.count}
            </TableCell>
            <TableCell
              sx={{
                textAlign: "center",
                fontWeight: "bold",
                fontSize: "1.4rem",
              }}
            >
              {grandTotals.specialCount}
            </TableCell>
            <TableCell
              sx={{
                textAlign: "center",
                fontWeight: "bold",
                fontSize: "1.4rem",
              }}
            >
              {grandTotals.premiumCount}
            </TableCell>
            <TableCell
              sx={{
                textAlign: "center",
                fontWeight: "bold",
                fontSize: "1.4rem",
              }}
            >
              {grandTotals.oneWeekAccomCount}
            </TableCell>
            <TableCell
              sx={{
                textAlign: "center",
                fontWeight: "bold",
                fontSize: "1.4rem",
              }}
            >
              {grandTotals.twoWeekAccomCount}
            </TableCell>
            <TableCell
              sx={{
                textAlign: "center",
                fontWeight: "bold",
                fontSize: "1.4rem",
              }}
            >
              {grandTotals.premiumAccomCount}
            </TableCell>
            <TableCell
              sx={{
                textAlign: "center",
                fontWeight: "bold",
                fontSize: "1.4rem",
              }}
            >
              {grandTotals.premiumOrTwoWeekAccomCount}
            </TableCell>
            <TableCell
              sx={{
                textAlign: "center",
                fontWeight: "bold",
                fontSize: "1.2rem",
              }}
            >
              {grandTotals.oneWeekCapacity} cap, {grandTotals.count} booked
              <br></br>
              {grandTotals.oneWeekCapacity - grandTotals.count} left
            </TableCell>
            <TableCell
              sx={{
                textAlign: "center",
                fontWeight: "bold",
                fontSize: "1.2rem",
              }}
            >
              {grandTotals.twoWeekCapacity} cap, {grandTotals.specialCount}{" "}
              booked
              <br></br>
              {grandTotals.oneWeekCapacity - grandTotals.count <
              grandTotals.twoWeekCapacity - grandTotals.specialCount
                ? grandTotals.oneWeekCapacity - grandTotals.count
                : grandTotals.twoWeekCapacity - grandTotals.specialCount}{" "}
              left
            </TableCell>
          </TableRow>

          {/* Render the rest of the rows */}
          {sortedData.map((entry, index, array) => {
            const isNewDate =
              index === 0 ||
              entry.ProgrammeDate !== array[index - 1].ProgrammeDate;

            // Calculate totals for the current date
            let dateTotals = {
              count: 0,
              specialCount: 0,
              premiumCount: 0,
              oneWeekAccomCount: 0,
              twoWeekAccomCount: 0,
              premiumAccomCount: 0,
              premiumOrTwoWeekAccomCount: 0,
              oneWeekCapacity: 0,
              twoWeekCapacity: 0,
            };

            array
              .filter((item) => item.ProgrammeDate === entry.ProgrammeDate)
              .forEach((item) => {
                dateTotals.count += item.count;
                dateTotals.specialCount += item.specialCount;
                dateTotals.premiumCount += item.premiumCount;
                dateTotals.oneWeekAccomCount += item.oneWeekAccomCount;
                dateTotals.twoWeekAccomCount += item.twoWeekAccomCount;
                dateTotals.premiumAccomCount += item.premiumAccomCount;
                dateTotals.premiumOrTwoWeekAccomCount +=
                  item.premiumOrTwoWeekAccomCount;
                dateTotals.oneWeekCapacity += item.oneWeekCapacity;
                dateTotals.twoWeekCapacity += item.twoWeekCapacity;
              });

            return (
              <React.Fragment key={index}>
                {/* Add a grey row if it's a new date */}
                {isNewDate && (
                  <TableRow>
                    <TableCell
                      colSpan={9}
                      sx={{
                        backgroundColor: "#f0f0f0",
                        height: "10px",
                      }}
                    ></TableCell>
                  </TableRow>
                )}
                <TableRow>
                  <TableCell
                    sx={{
                      textAlign: "center",
                      fontWeight: "bold",
                    }}
                  >
                    {entry.ProgrammeDate}
                  </TableCell>
                  <TableCell
                    sx={{
                      textAlign: "center",
                      fontSize: "1.0rem",
                    }}
                  >
                    {entry.ProgrammeTitle}
                  </TableCell>
                  <TableCell
                    sx={{
                      textAlign: "center",
                      fontWeight: "bold",
                      fontSize: "1.1rem",
                    }}
                  >
                    {entry.count}
                  </TableCell>
                  <TableCell
                    sx={{
                      textAlign: "center",
                      fontWeight: "bold",
                      fontSize: "1.1rem",
                    }}
                  >
                    {entry.specialCount}
                  </TableCell>
                  <TableCell
                    sx={{
                      textAlign: "center",
                      fontWeight: "bold",
                      fontSize: "1.1rem",
                    }}
                  >
                    {entry.premiumCount}
                  </TableCell>
                  <TableCell
                    sx={{
                      textAlign: "center",
                      fontWeight: "bold",
                      fontSize: "1.1rem",
                    }}
                  >
                    {entry.oneWeekAccomCount}
                  </TableCell>
                  <TableCell
                    sx={{
                      textAlign: "center",
                      fontWeight: "bold",
                      fontSize: "1.1rem",
                    }}
                  >
                    {entry.twoWeekAccomCount}
                  </TableCell>
                  <TableCell
                    sx={{
                      textAlign: "center",
                      fontWeight: "bold",
                      fontSize: "1.1rem",
                    }}
                  >
                    {entry.premiumAccomCount}
                  </TableCell>
                  <TableCell
                    sx={{
                      textAlign: "center",
                      fontWeight: "bold",
                      fontSize: "1.1rem",
                    }}
                  >
                    {entry.premiumOrTwoWeekAccomCount}
                  </TableCell>
                  <TableCell
                    sx={{
                      textAlign: "center",
                      fontWeight: "bold",
                      fontSize: "1.1rem",
                      color:
                        entry.oneWeekCapacity - entry.count < 1
                          ? "red"
                          : entry.oneWeekCapacity - entry.count < 6
                          ? "orange"
                          : "rgb(15, 131, 0)",
                    }}
                  >
                    {entry.oneWeekCapacity} cap, {entry.count} booked
                    <br></br>
                    {entry.oneWeekCapacity - entry.count} left
                  </TableCell>
                  <TableCell
                    sx={{
                      textAlign: "center",
                      fontWeight: "bold",
                      fontSize: "1.1rem",
                      color:
                        (entry.oneWeekCapacity - entry.count <
                        entry.twoWeekCapacity - entry.specialCount
                          ? entry.oneWeekCapacity - entry.count
                          : entry.twoWeekCapacity - entry.specialCount) < 1
                          ? "red"
                          : (entry.oneWeekCapacity - entry.count <
                            entry.twoWeekCapacity - entry.specialCount
                              ? entry.oneWeekCapacity - entry.count
                              : entry.twoWeekCapacity - entry.specialCount) < 6
                          ? "orange"
                          : "rgb(15, 131, 0)",
                    }}
                  >
                    {entry.twoWeekCapacity} cap, {entry.specialCount} booked
                    <br></br>
                    {entry.oneWeekCapacity - entry.count <
                    entry.twoWeekCapacity - entry.specialCount
                      ? entry.oneWeekCapacity - entry.count
                      : entry.twoWeekCapacity - entry.specialCount}{" "}
                    left
                  </TableCell>
                </TableRow>
                {/* Add a total row for the current date */}
                {index === array.length - 1 ||
                entry.ProgrammeDate !== array[index + 1].ProgrammeDate ? (
                  <TableRow>
                    <TableCell
                      colSpan={2}
                      sx={{
                        textAlign: "center",
                        fontWeight: "bold",
                        backgroundColor: "#f0f0f0",
                        fontSize: "1.3rem",
                      }}
                    >
                      Total for {entry.ProgrammeDate}
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: "center",
                        fontWeight: "bold",
                        fontSize: "1.3rem",
                      }}
                    >
                      {dateTotals.count}
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: "center",
                        fontWeight: "bold",
                        fontSize: "1.3rem",
                      }}
                    >
                      {dateTotals.specialCount}
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: "center",
                        fontWeight: "bold",
                        fontSize: "1.3rem",
                      }}
                    >
                      {dateTotals.premiumCount}
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: "center",
                        fontWeight: "bold",
                        fontSize: "1.3rem",
                      }}
                    >
                      {dateTotals.oneWeekAccomCount}
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: "center",
                        fontWeight: "bold",
                        fontSize: "1.3rem",
                      }}
                    >
                      {dateTotals.twoWeekAccomCount}
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: "center",
                        fontWeight: "bold",
                        fontSize: "1.3rem",
                      }}
                    >
                      {dateTotals.premiumAccomCount}
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: "center",
                        fontWeight: "bold",
                        fontSize: "1.3rem",
                      }}
                    >
                      {dateTotals.premiumOrTwoWeekAccomCount}
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: "center",
                        fontWeight: "bold",
                        fontSize: "1.3rem",
                      }}
                    >
                      {dateTotals.oneWeekCapacity} cap, {dateTotals.count}{" "}
                      booked
                      <br></br>
                      {dateTotals.oneWeekCapacity - dateTotals.count} left
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: "center",
                        fontWeight: "bold",
                        fontSize: "1.3rem",
                      }}
                    >
                      {dateTotals.twoWeekCapacity} cap,{" "}
                      {dateTotals.specialCount} booked
                      <br></br>
                      {dateTotals.oneWeekCapacity - dateTotals.count <
                      dateTotals.twoWeekCapacity - dateTotals.specialCount
                        ? dateTotals.oneWeekCapacity - dateTotals.count
                        : dateTotals.twoWeekCapacity -
                          dateTotals.specialCount}{" "}
                      left
                    </TableCell>
                  </TableRow>
                ) : null}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default TableRegisters;
